import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  AppointmentTemplateDto,
  AppointmentDto,
  BatchCancelDeleteAppointmentDto,
  PatientAvailabilityDto,
  TimeSlotDto,
  UpdateAppointmentDto,
  UpdateCancelledAppointmentDto,
  ContinuousAppointmentDto,
  CAResponse,
  GapJustificationDto,
  AvailabilityRequestDto,
  CreateAndScheduleAppointmentDto,
  PatientTherapistPreferenceDto,
  PatientInformingChannel,
} from "../../api-client";
import { useApi } from "../../api/ApiProvider";
import { appointmentKeys } from "./appointment.keys";
import { rxKeys } from "../Rx";
import { calendarKeys, invalidateCalendarAppointmentCaches } from "../Calendar/calendar.keys";
import {
  ContinuousCacheSnapshot,
  beginAppointmentMutation,
  isLatestAppointmentMutation,
  optimisticContinuousAppointmentUpsert,
  restoreContinuousCaches,
} from "../Calendar/AppointmentsOfAllTypes.utils";
import { transactionKeys } from "../Transactions/transaction.keys";
import { useLocationId } from "../User/User.hooks";
import useStore from "../../helpers/useStore";
import { PatientsContext } from "../Patients/patients.provider";
import { CalendarContext } from "../Calendar/calendar.provider";
import moment from "moment";

type CancelledAppointmentKey = { locationId?: string; patientId?: number };

/**
 * Single-user (date-range) view embeds group participants inside the
 * GroupTimeSlotTherapist umbrella appointment. When the last participant is
 * cancelled, prune the now-empty umbrella from the appointmentsForDateRange
 * cache so the empty group disappears immediately (mirrors the move-out
 * behaviour). Only the single-therapist view consumes appointmentsForDateRange,
 * so this cleanup is naturally scoped to that view.
 */
const pruneEmptyGroupAnchorFromRangeCache = (
  queryClient: ReturnType<typeof useQueryClient>,
  cancelledId: string,
) => {
  const matchesCancelled = (participant: any): boolean => {
    if (!participant) return false;
    const firstTimeSlot =
      participant.timeSlots?.[0] || (participant.type ? participant : undefined);
    return (
      String(participant.id) === String(cancelledId) ||
      String(participant.appointmentId) === String(cancelledId) ||
      String(firstTimeSlot?.id) === String(cancelledId) ||
      String(firstTimeSlot?.appointmentId) === String(cancelledId)
    );
  };

  queryClient.setQueriesData(
    { queryKey: calendarKeys.appointmentsForDateRange.all() },
    (old: any) => {
      if (!old?.appointments) return old;
      let changed = false;
      const appointments = old.appointments
        .map((appointment: any) => {
          const isGroupTherapist = appointment.timeSlots?.some(
            (ts: any) => ts.type === "GroupTimeSlotTherapist",
          );
          if (!isGroupTherapist || !appointment.participants?.length) return appointment;
          const remaining = appointment.participants.filter((p: any) => !matchesCancelled(p));
          if (remaining.length === appointment.participants.length) return appointment;
          changed = true;
          // Drop the umbrella once its last participant is cancelled.
          if (remaining.length === 0) return null;
          return { ...appointment, participants: remaining };
        })
        .filter(Boolean);
      if (!changed) return old;
      return { ...old, appointments };
    },
  );
};

export const useUpdateAppointment = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    appointmentId: string;
    updateAppointmentDto: UpdateAppointmentDto;
    isContinuous: boolean;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "updateAppointment"],
    mutationFn: async ({ appointmentId, updateAppointmentDto, isContinuous, patientId }: Vars) => {
      const response = await scheduler.schedulerReschedule(
        appointmentId,
        updateAppointmentDto,
        undefined,
        isContinuous,
      );
      return response;
    },
    onSuccess: async (data, variables) => {
      const invalidations = [];
      const eventDate = moment(variables.updateAppointmentDto?.start!).toDate();

      // Optimistically update the single-therapist date-range cache so the edited
      // appointment reflects the changes without waiting for a full refetch.
      queryClient.setQueriesData(
        { queryKey: calendarKeys.appointmentsForDateRange.all() },
        (old: any) => {
          if (!old?.appointments) return old;
          const hasAppointment = old.appointments.some(
            (appt: AppointmentDto) => String(appt.id) === String(variables.appointmentId),
          );
          if (!hasAppointment) return old;
          return {
            ...old,
            appointments: old.appointments.map((appt: AppointmentDto) =>
              String(appt.id) === String(variables.appointmentId)
                ? { ...appt, ...variables.updateAppointmentDto }
                : appt,
            ),
          };
        },
      );

      if (
        variables.updateAppointmentDto?.timeSlots?.some(
          (ts) =>
            ts.type === "GroupTimeSlotPatient" || ts.type === "ContinuousGroupTimeSlotPatient",
        )
      ) {
        await queryClient.invalidateQueries({
          queryKey: calendarKeys.appointmentsForDate.detail(eventDate),
        });
      }

      if (variables.isContinuous) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.continuousAppointmentsByPatient.all(),
          }),
        );
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
          }),
        );
      } else {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.appointmentsByRx.all(),
          }),
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.appointmentsByPatient.detail(variables.patientId),
          }),
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.appointmentsBySeries.all(),
          }),
          queryClient.invalidateQueries({
            queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
          }),
          queryClient.invalidateQueries({
            queryKey: transactionKeys.unpaidBadgeCount.forPatient(variables.patientId),
          }),
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.serviceAppointmentsByPatient.detail(variables.patientId),
          }),
        );
      }

      await Promise.all(invalidations);
    },
    meta: {
      successMessage: "Termin wurde aktualisiert",
      errorMessage: "Fehler beim Aktualisieren des Termins",
    } as const,
  });
};

export const useUpdateMultipleAppointments = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    updateAppointmentDto: UpdateAppointmentDto[];
    isContinuous: boolean;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "updateMultipleAppointments"],
    mutationFn: async ({ updateAppointmentDto, isContinuous, patientId }: Vars) => {
      const response = await scheduler.schedulerRescheduleMultiple(
        updateAppointmentDto,
        isContinuous,
      );
      return response;
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsBySeries.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(variables.patientId),
        }),
      ]);

      if (variables.isContinuous) {
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
        });
      }
    },
    meta: {
      successMessage: "Termine wurden aktualisiert",
      errorMessage: "Fehler beim Aktualisieren der Termine",
    } as const,
  });
};
export const useDeleteHardTimeslot = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    timeSlotId: string;
    isContinuous: boolean;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "deleteHardTimeslot"],
    mutationFn: async ({ timeSlotId, isContinuous, patientId }: Vars) => {
      const response = await scheduler.schedulerDeleteTimeSlot(timeSlotId, isContinuous);
      return response;
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(variables.patientId),
        }),
      ]);

      if (variables.isContinuous) {
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
        });
      }
    },
    meta: {
      successMessage: "Zeitslot wurde gelöscht",
      errorMessage: "Fehler beim Löschen des Zeitslots",
    } as const,
  });
};

export const useCreateForTreatments = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    appointmentTemplateDto: AppointmentTemplateDto;
    isContinuous?: boolean;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "createForTreatments"],
    mutationFn: async ({ appointmentTemplateDto, isContinuous, patientId }: Vars) => {
      const response = await appointments.createForTreatments(appointmentTemplateDto, isContinuous);
      return response;
    },
    onSuccess: async (data, variables) => {
      const invalidations = [
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsBySeries.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxLackingAppointments.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(variables.patientId),
        }),
      ];

      if (variables.isContinuous) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.continuousAppointmentsByPatient.all(),
          }),
        );
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
          }),
        );
      }

      await Promise.all(invalidations);
    },
    meta: {
      successMessage: "Termine wurden erstellt",
      errorMessage: "Fehler beim Erstellen der Termine",
    } as const,
  });
};

/**
 * MD-148 Dynamic Template Provider: create one appointment from prescribed
 * therapies and schedule it at the dropped slot in a single request, then
 * refresh the calendar, the unscheduled shelf and the lacking-appointment
 * counts that drive the widget's handles.
 */
export const useCreateAndScheduleAppointment = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    dto: CreateAndScheduleAppointmentDto;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "createAndSchedule"],
    mutationFn: async ({ dto }: Vars) => {
      const response = await appointments.createAndScheduleAppointment(dto);
      return response;
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        invalidateCalendarAppointmentCaches(queryClient),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledPatientAppointments.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxLackingAppointments.forPatient(variables.patientId),
        }),
      ]);
    },
    meta: {
      successMessage: "Termin wurde erstellt und geplant",
      errorMessage: "Fehler beim Erstellen und Planen des Termins",
    } as const,
  });
};

export const useSavePatientUnavailibilityForPatient = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    patientId: number;
    unavailabilityData: PatientAvailabilityDto[];
  };
  return useMutation({
    mutationKey: ["appointments", "savePatientUnavailibilityForPatient"],
    mutationFn: async ({ patientId, unavailabilityData }: Vars) => {
      const response = await appointments.savePatientUnavailibilityForPatient(
        patientId,
        unavailabilityData,
      );
      return response;
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.patientUnavailabilityByPatient.detail(
            variables.patientId.toString(),
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.patientUnavailabilityForCalendar.all(),
        }),
      ]);
    },
    meta: {
      successMessage: "Die Unverfügbarkeit wurde gespeichert",
      errorMessage: "Fehler beim Speichern der Unverfügbarkeit",
    } as const,
  });
};

export const useDeletePatientUnavailability = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    groupId: string;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "deletePatientUnavailability"],
    mutationFn: async ({ groupId, patientId }: Vars) => {
      const response = await appointments.deletePatientUnavailability(groupId);
      return response;
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.patientUnavailabilityByPatient.detail(
            variables.patientId.toString(),
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.patientUnavailabilityForCalendar.all(),
        }),
      ]);
    },
    meta: {
      successMessage: "Die Unverfügbarkeit wurde gelöscht",
      errorMessage: "Fehler beim Löschen der Unverfügbarkeit",
    } as const,
  });
};

export const useSaveBlockedSlotForPatient = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    patientId: number;
    blockedSlots: TimeSlotDto[];
  };
  return useMutation({
    mutationKey: ["appointments", "saveBlockedSlotForPatient"],
    mutationFn: async ({ patientId, blockedSlots }: Vars) => {
      const response = await appointments.saveBlockedSlotsForPatient(patientId, blockedSlots);
      return response;
    },
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.blockedSlotsByPatient.detail(variables.patientId.toString()),
        }),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.patientUnavailabilityTypeByDateForCalendar.all(),
        }),
      ]);
    },
    meta: {
      successMessage: "Der blockierte Zeitraum wurde gespeichert",
      errorMessage: "Fehler beim Speichern des blockierten Zeitraums",
    } as const,
  });
};

export const useDeleteBlockedSlot = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    timeSlotId: string;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "deleteBlockedSlot"],
    mutationFn: async ({ timeSlotId, patientId }: Vars) => {
      await appointments.deleteBlockedSlot(timeSlotId);
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.blockedSlotsByPatient.detail(variables.patientId.toString()),
        }),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.patientUnavailabilityTypeByDateForCalendar.all(),
        }),
      ]);
    },
    meta: {
      successMessage: "Der blockierte Zeitraum wurde gelöscht",
      errorMessage: "Fehler beim Löschen des blockierten Zeitraums",
    } as const,
  });
};

export const useCreateTherapistListEntry = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    patientId: number;
    entry: PatientTherapistPreferenceDto;
  };
  return useMutation({
    mutationKey: ["appointments", "createTherapistListEntry"],
    mutationFn: async ({ patientId, entry }: Vars) => {
      return await appointments.createTherapistListEntry(patientId, entry);
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: appointmentKeys.therapistListByPatient.detail(variables.patientId.toString()),
      });
    },
    meta: {
      successMessage: "Die Therapeuten-Präferenz wurde gespeichert",
      errorMessage: "Fehler beim Speichern der Therapeuten-Präferenz",
    } as const,
  });
};

export const useUpdateTherapistListEntry = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    id: string;
    patientId: number;
    entry: PatientTherapistPreferenceDto;
  };
  return useMutation({
    mutationKey: ["appointments", "updateTherapistListEntry"],
    mutationFn: async ({ id, entry }: Vars) => {
      return await appointments.updateTherapistListEntry(id, entry);
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: appointmentKeys.therapistListByPatient.detail(variables.patientId.toString()),
      });
    },
    meta: {
      successMessage: "Die Therapeuten-Präferenz wurde aktualisiert",
      errorMessage: "Fehler beim Aktualisieren der Therapeuten-Präferenz",
    } as const,
  });
};

export const useDeleteTherapistListEntry = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    id: string;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "deleteTherapistListEntry"],
    mutationFn: async ({ id }: Vars) => {
      await appointments.deleteTherapistListEntry(id);
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: appointmentKeys.therapistListByPatient.detail(variables.patientId.toString()),
      });
    },
    meta: {
      successMessage: "Der Eintrag wurde gelöscht",
      errorMessage: "Fehler beim Löschen des Eintrags",
    } as const,
  });
};

export const useSetAttended = () => {
  //TO CHECK WITH DAVID
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    appointmentId: string;
    attended: boolean;
    patientId: number;
    appointmentStart: Date;
  };
  return useMutation({
    mutationKey: ["appointments", "setAttended"],
    mutationFn: async ({ appointmentId, attended, patientId }: Vars) => {
      const response = await appointments.setAttended(appointmentId, attended);
      return response;
    },
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: [appointmentKeys.appointmentsByPatient.scope],
        }),
        queryClient.invalidateQueries({
          queryKey: [appointmentKeys.appointmentsByRx.scope],
        }),
        queryClient.invalidateQueries({
          queryKey: [appointmentKeys.appointmentsBySeries.scope],
        }),
        queryClient.invalidateQueries({ queryKey: [rxKeys.rxList.scope] }),
        queryClient.invalidateQueries({ queryKey: [rxKeys.rx.scope] }),
        // Every cached day, not only this appointment's: attendance drives the
        // informing badge on the patient's next appointment, which sits on another day.
        invalidateCalendarAppointmentCaches(queryClient),
      ]);
    },
    meta: {
      successMessage: "Anwesenheit wurde gespeichert",
      errorMessage: "Fehler beim Markieren des Termins als besucht",
    } as const,
  });
};

/** Shared invalidation for informing mutations (mirrors useSetAttended scopes). */
const invalidateInformingQueries = async (
  queryClient: ReturnType<typeof useQueryClient>,
  appointmentStart?: Date
) => {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: [appointmentKeys.appointmentsByPatient.scope] }),
    queryClient.invalidateQueries({ queryKey: [appointmentKeys.appointmentsByRx.scope] }),
    queryClient.invalidateQueries({ queryKey: [appointmentKeys.appointmentsBySeries.scope] }),
    queryClient.invalidateQueries({ queryKey: [rxKeys.rxList.scope] }),
    queryClient.invalidateQueries({ queryKey: [rxKeys.rx.scope] }),
    ...(appointmentStart
      ? [
          queryClient.invalidateQueries({
            queryKey: calendarKeys.appointmentsForDate.detail(appointmentStart),
          }),
        ]
      : []),
    queryClient.invalidateQueries({ queryKey: calendarKeys.appointmentsForDateRange.all() }),
  ]);
};

/**
 * Set or reset whether the patient has been informed about an appointment.
 * `informed: true` records a MANUAL informing per slot; `false` voids it.
 */
export const useSetAppointmentInformed = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    appointmentId: string;
    informed: boolean;
    appointmentStart?: Date;
  };
  return useMutation({
    mutationKey: ["appointments", "setAppointmentInformed"],
    mutationFn: async ({ appointmentId, informed }: Vars) => {
      return appointments.setAppointmentInformed({ appointmentId, informed });
    },
    onSuccess: (_data, variables) => {
      // Fire-and-forget: do NOT await. invalidateQueries refetches the active
      // (mounted) queries — patient/rx cards and especially the whole calendar
      // date-range — and awaiting it would keep the dialog open until those
      // refetches finish. Let them refresh in the background so the dialog
      // closes as soon as the write returns.
      void invalidateInformingQueries(queryClient, variables.appointmentStart);
    },
    meta: {
      successMessage: "Informationsstatus wurde gespeichert",
      errorMessage: "Fehler beim Speichern des Informationsstatus",
    } as const,
  });
};

/** Mark the given timeslots as informed via a printout (print confirm flow). */
export const useRecordInformingPrintout = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    timeSlotIds: string[];
    appointmentStart?: Date;
  };
  return useMutation({
    mutationKey: ["appointments", "recordInformingPrintout"],
    mutationFn: async ({ timeSlotIds }: Vars) => {
      return appointments.recordPatientInforming({
        timeSlotIds,
        channel: PatientInformingChannel.Printout,
      });
    },
    onSuccess: async (_data, variables) => {
      await invalidateInformingQueries(queryClient, variables.appointmentStart);
    },
    meta: {
      // successMessage: "Patient(en) als informiert markiert",
      // errorMessage: "Fehler beim Markieren als informiert",
    } as const,
  });
};

export const useAttendTimeSlot = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    slotId: string;
    attended: boolean;
    patientId: number;
    appointmentStart: Date;
  };
  return useMutation({
    mutationKey: ["appointments", "attendTimeSlot"],
    mutationFn: async ({ slotId, attended, patientId }: Vars) => {
      const response = await appointments.setTimeSlotAttended(slotId, attended);
      return response;
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsBySeries.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.all(),
        }),
        queryClient.invalidateQueries({
          //because status changes
          queryKey: rxKeys.rxList.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rx.all(),
        }),
        // Every cached day, not only this appointment's: attendance drives the
        // informing badge on the patient's next appointment, which sits on another day.
        invalidateCalendarAppointmentCaches(queryClient),
      ]);
    },
    meta: {
      successMessage: "Anwesenheit wurde gespeichert",
      errorMessage: "Fehler beim Markieren des Termins als besucht",
    } as const,
  });
};

export const useCancelAppointment = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  const locationId = useLocationId();
  type Vars = {
    appointmentId: string;
    byTherapist: boolean;
    cancelAppDto: UpdateCancelledAppointmentDto;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "cancelAppointment"],
    mutationFn: async ({ appointmentId, byTherapist, cancelAppDto, patientId }: Vars) => {
      const response = await scheduler.schedulerCancelAppointment(
        appointmentId,
        byTherapist,
        cancelAppDto,
      );
      return response;
    },
    onSuccess: async (data, variables) => {
      const invalidations = [
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsBySeries.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledPatientAppointments.detail(variables.patientId),
        }),

        // cancelling changes the rx validation result (gaps, treatment count, ...)
        // and may auto-create a gap justification, so refresh the rx/validation cache
        queryClient.invalidateQueries({ queryKey: [rxKeys.rx.scope] }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxList.forPatient(variables.patientId),
        }),

        // invalidate calendar cache (TODO could be made more efficient)
        // TODO we need access to the date of the appointment to only invalidate the relevant day
        // Invalidates both the main day cache and the single-therapist
        // (date-range) cache so the cancelled appointment (end === null) is
        // filtered out of both views.
        invalidateCalendarAppointmentCaches(queryClient),
      ];

      if (locationId) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.cancelledAppointments.detail(locationId),
          }),
        );
      }

      // Single-user view: drop the empty group umbrella when its last
      // participant is cancelled.
      pruneEmptyGroupAnchorFromRangeCache(queryClient, variables.appointmentId);

      await Promise.all(invalidations);
    },
    meta: {
      successMessage: "Der Termin wurde storniert",
      errorMessage: "Fehler beim Stornieren des Termins",
    } as const,
  });
};

export const useCancelTimeSlot = () => {
  const { scheduler } = useApi();

  const queryClient = useQueryClient();
  const locationId = useLocationId();
  type Vars = {
    slotId: string;
    byTherapist: boolean;
    cancelAppDto: UpdateCancelledAppointmentDto;

    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "cancelTimeSlot"],
    mutationFn: async ({ slotId, byTherapist, cancelAppDto, patientId }: Vars) => {
      const response = await scheduler.schedulerCancelTimeSlot(slotId, byTherapist, cancelAppDto);
      return response;
    },
    onSuccess: async (data, variables) => {
      const invalidations = [
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsBySeries.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(variables.patientId),
        }),

        // invalidate calendar cache (TODO could be made more efficient)
        // TODO we need access to the date of the appointment to only invalidate the relevant day
        // Invalidates both the main day cache and the single-therapist
        // (date-range) cache so the cancelled time slot (end === null) is
        // filtered out of both views.
        invalidateCalendarAppointmentCaches(queryClient),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledPatientAppointments.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointmentsByPatient.detail(variables.patientId),
        }),

        // cancelling changes the rx validation result (gaps, treatment count, ...)
        // and may auto-create a gap justification, so refresh the rx/validation cache
        queryClient.invalidateQueries({ queryKey: [rxKeys.rx.scope] }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxList.forPatient(variables.patientId),
        }),
      ];
      if (locationId) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.cancelledAppointments.detail(locationId),
          }),
        );
      }

      // Single-user view: drop the empty group umbrella when its last
      // participant is cancelled.
      pruneEmptyGroupAnchorFromRangeCache(queryClient, variables.slotId);

      await Promise.all(invalidations);
    },
    meta: {
      successMessage: "Der Termin wurde storniert",
      errorMessage: "Fehler beim Stornieren des Termins",
    } as const,
  });
};

export const useBatchCancelAndDeleteAppointments = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    batchCancelAndDeleteAppointments: BatchCancelDeleteAppointmentDto;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "batchCancelAndDeleteAppointments"],
    mutationFn: async ({ batchCancelAndDeleteAppointments, patientId }: Vars) => {
      const response = await scheduler.schedulerBatchCancelAndDeleteAppointments(
        batchCancelAndDeleteAppointments,
      );
      return response;
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsBySeries.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointmentsByPatient.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(variables.patientId),
        }),

        // cancelling changes the rx validation result, refresh the rx/validation cache
        queryClient.invalidateQueries({ queryKey: [rxKeys.rx.scope] }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxList.forPatient(variables.patientId),
        }),

        // invalidate calendar cache (TODO could be made more efficient)
        // TODO we need access to the date of the appointment to only invalidate the relevant day
        // Invalidates both the main day cache and the single-therapist
        // (date-range) cache so the cancelled appointments are filtered out of
        // both views.
        invalidateCalendarAppointmentCaches(queryClient),
      ]);
    },
    meta: {
      successMessage: "Die Termine wurden erfolgreich storniert",
      errorMessage: "Fehler beim Stornieren der Termine",
    } as const,
  });
};

export const useBatchCancelAndDeleteTimeSlots = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    batchCancelAndDeleteTimeSlots: BatchCancelDeleteAppointmentDto;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "batchCancelAndDeleteTimeSlots"],
    mutationFn: async ({ batchCancelAndDeleteTimeSlots, patientId }: Vars) => {
      const response = await scheduler.schedulerBatchCancelAndDeleteTimeSlots(
        batchCancelAndDeleteTimeSlots,
      );
      return response;
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: appointmentKeys.appointmentsByPatient.all() }),
        queryClient.invalidateQueries({ queryKey: appointmentKeys.appointmentsByRx.all() }),
        queryClient.invalidateQueries({ queryKey: appointmentKeys.appointmentsBySeries.all() }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointmentsByPatient.all(),
        }),

        // cancelling changes the rx validation result, refresh the rx/validation cache
        queryClient.invalidateQueries({ queryKey: [rxKeys.rx.scope] }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxList.forPatient(variables.patientId),
        }),
      ]);
    },
    meta: {
      successMessage: "Die Termine wurden erfolgreich storniert",
      errorMessage: "Fehler beim Stornieren der Termine",
    } as const,
  });
};

export const useCancelContinuous = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    appointmentId: string;
  };
  return useMutation({
    mutationKey: ["appointments", "cancelContinuous"],
    mutationFn: async ({ appointmentId }: Vars) => {
      const response = await scheduler.schedulerUnscheduleContinuousAppointment(appointmentId);
      return response;
    },
    onSuccess: async (data, variables) => {
      // console.log("Invalidating continuous appointments cache after useCancelContinuous");
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.continuousAppointmentsByPatient.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.continuousAppointmentsByDay.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
        }),
      ]);
    },
    meta: {
      successMessage: "Der Dauertermin wurde storniert",
      errorMessage: "Fehler beim Stornieren des Dauertermins",
    } as const,
  });
};

export const useDeleteContinous = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    appointmentId: string;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "deleteContinous"],
    mutationFn: async ({ appointmentId }: Vars) => {
      const response = await scheduler.schedulerDeleteContinuousAppointment(appointmentId);
      return response;
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.continuousAppointmentsByPatient.detail(
            variables.patientId.toString(),
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxLackingAppointments.forPatient(variables.patientId),
        }),
      ]);

      queryClient.invalidateQueries({
        queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
      });
    },
    meta: {
      successMessage: "Der Dauertermin wurde gelöscht",
      errorMessage: "Fehler beim Löschen des Dauertermins",
    } as const,
  });
};

export const useDeleteAppointment = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    appointmentId: string;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "deleteAppointment"],
    mutationFn: async ({ appointmentId }: Vars) => {
      const response = await scheduler.schedulerDeleteAppointment(appointmentId);
      return response;
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({ queryKey: appointmentKeys.appointmentsByRx.all() }),
        queryClient.invalidateQueries({ queryKey: appointmentKeys.appointmentsBySeries.all() }),
        queryClient.invalidateQueries({
          queryKey: [rxKeys.rxLackingAppointments.scope],
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(variables.patientId),
        }),
      ]);
    },
    meta: {
      successMessage: "Der Termin wurde gelöscht",
      errorMessage: "Fehler beim Löschen des Termins",
    } as const,
  });
};

export const useDeleteUnscheduledAppointments = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "deleteUnscheduledAppointment"],
    mutationFn: async ({ patientId }: Vars) => {
      const response = await scheduler.schedulerDeleteUnscheduledAppointments(patientId);
      return response;
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxLackingAppointments.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.all(),
        }),
      ]);
    },
    meta: {
      successMessage: "Der ungeplante Termin wurde gelöscht",
      errorMessage: "Fehler beim Löschen des ungeplanten Termins",
    } as const,
  });
};

export const useCopyNewAppointment = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    appointmentId: string;
    patientId: number;
    isContinuous: boolean;
    timeSlotIds?: string[];
  };
  return useMutation({
    mutationKey: ["appointments", "copyNewAppointment"],
    mutationFn: async ({ appointmentId, isContinuous, timeSlotIds }: Vars) => {
      const response = await appointments.copyToNew(
        appointmentId,
        isContinuous,
        timeSlotIds && timeSlotIds.length > 0 ? { timeSlotIds } : undefined,
      );
      return response;
    },
    onSuccess: async (data, variables) => {
      const invalidations = [];

      if (variables.isContinuous) {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.continuousAppointmentsByPatient.all(),
          }),
        );
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
          }),
        );
      } else {
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.appointmentsByPatient.all(),
          }),
        );

        // invalidate appointments in event shelf
        invalidations.push(
          queryClient.invalidateQueries({
            queryKey: calendarKeys.unscheduledPatientAppointments.detail(variables.patientId),
          }),
        );
      }

      await Promise.all(invalidations);
    },
    meta: {
      successMessage: "Der Termin wurde kopiert",
      errorMessage: "Fehler beim Kopieren des Termins",
    } as const,
  });
};

export const useCancellationOfCancelledAppointment = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  const patientStore = useStore(PatientsContext);
  type Vars = {
    appointmentId: string;
    resolved: boolean;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "cancellationOfCancelledAppointment"],
    mutationFn: async ({ appointmentId, resolved, patientId }: Vars) => {
      const response = await appointments.cancellationOfCancelledAppointment(
        appointmentId,
        resolved,
      );
      return response;
    },
    onSuccess: async (data, variables) => {
      const seriesIds: any = (data as any)?.seriesIds;
      if (seriesIds?.length > 0) {
        patientStore.setPreflightDialogOpen(true, seriesIds);
      }
      await Promise.all([
        // we must invalidate the main cancelled appointments list
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointments.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxLackingAppointments.forPatient(variables.patientId),
        }),

        // Resolving a cancellation takes the appointment off the calendar: the
        // calendar queries select `resolved = false`, so the server stops
        // returning it. Every sibling cancel mutation invalidates these caches;
        // this one did not, which is why the entry sat on the calendar until a
        // reload. The socket broadcast alone cannot cover it — it is throttled
        // behind a 20s/idle flush gate, so the tab that acted needs this.
        invalidateCalendarAppointmentCaches(queryClient),

        // Resolving moves the appointment BETWEEN lists rather than ending it:
        // the cancellation stops counting, and a non-perpetual appointment is
        // left with start/end NULL — which is this codebase's definition of
        // "unscheduled". So the unscheduled list gains an entry at the same
        // moment the cancelled one loses it, and both have to be refreshed.
        // (A perpetual Rx is deleted outright instead; then this is a no-op.)
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledPatientAppointments.detail(variables.patientId),
        }),

        // resolving changes the rx validation result, refresh the rx/validation cache
        queryClient.invalidateQueries({ queryKey: [rxKeys.rx.scope] }),
        queryClient.invalidateQueries({
          queryKey: rxKeys.rxList.forPatient(variables.patientId),
        }),
      ]);
    },
    meta: {
      // Every caller passes `resolved: true` — this is "Ausfallen lassen"
      // (let the appointment lapse), not an undo of the cancellation.
      successMessage: "Der Termin wurde als ausgefallen abgeschlossen",
      errorMessage: "Fehler beim Abschließen des ausgefallenen Termins",
    } as const,
  });
};

export const useUpdateCancelledAppointment = (key: CancelledAppointmentKey) => {
  const { appointments } = useApi();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (payload: { id: string; dto: UpdateCancelledAppointmentDto }) => {
      const data = await appointments.updateCancelledAppointment(payload.id, payload.dto);
      return data;
    },

    onSuccess: async (data) => {
      if (key.locationId) {
        qc.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointments.detail(key.locationId),
        });
      }
      if (key.patientId) {
        qc.invalidateQueries({
          queryKey: appointmentKeys.cancelledAppointmentsByPatient.detail(key.patientId),
        });
        qc.invalidateQueries({ queryKey: appointmentKeys.appointmentsByRx.all() });
        qc.invalidateQueries({ queryKey: appointmentKeys.appointmentsBySeries.all() });
      }
      if (data.originalDate) {
        qc.invalidateQueries({
          queryKey: calendarKeys.appointmentsForDate.detail(new Date(data.originalDate)),
        });
      }
    },
    meta: {
      successMessage: "Stornierung erfolgreich aktualisiert!",
      errorMessage: "Stornierung konnte nicht aktualisiert werden!",
    },
  });
};

export const useUpdateContinuousAppointment = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    appointmentId: string;
    appointment: ContinuousAppointmentDto;
  };
  return useMutation({
    mutationKey: ["appointments", "updateContinuousAppointment"],
    mutationFn: async ({ appointmentId, appointment }: Vars) => {
      const response = await scheduler.schedulerRescheduleContinuous(appointmentId, appointment);
      return response;
    },
    onMutate: async ({ appointmentId, appointment }) => {
      // Provisional cache merge across both continuous caches so the continuous
      // calendar agrees with FullCalendar's optimistic placement while the
      // request is in flight — a rebuild of the controlled events array can no
      // longer snap the continuous appointment back. Stamp with a per-appointment
      // sequence so an out-of-order completion of a stale move cannot clobber a
      // newer one (last-issued-wins).
      const seq = beginAppointmentMutation(appointmentId);
      const snapshot: ContinuousCacheSnapshot = await optimisticContinuousAppointmentUpsert(
        queryClient,
        { ...appointment, id: appointmentId },
      );
      return { snapshot, seq };
    },
    onError: (_error, variables, context) => {
      // Only the latest issued move owns the rollback; a stale failure must not
      // restore a snapshot that predates a newer, successful move.
      if (context && !isLatestAppointmentMutation(variables.appointmentId, context.seq)) {
        return;
      }
      if (context?.snapshot) {
        restoreContinuousCaches(queryClient, context.snapshot);
      }
    },
    onSuccess: async (data, variables, context) => {
      // Out-of-order guard: if a newer move of this same continuous appointment
      // has since been issued, this response is stale — skip invalidations so the
      // appointment does not snap back to this (older) server position.
      if (context && !isLatestAppointmentMutation(variables.appointmentId, context.seq)) {
        return;
      }
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.continuousAppointmentsByPatient.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.continuousAppointmentsByDay.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledContinuousAppointments.all(),
        }),
      ]);
    },
    meta: {
      successMessage: "Dauertermin wurde aktualisiert",
      errorMessage: "Dauertermin konnte nicht gespeichert werden",
    } as const,
  });
};

export const useBackfillContinuousAppointment = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    appointmentId: string;
  };
  return useMutation({
    mutationKey: ["appointments", "backfillContinuousAppointment"],
    mutationFn: async ({ appointmentId }: Vars) => {
      const response = await appointments.backFillContinuous(appointmentId);
      return response;
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.continuousAppointmentsByPatient.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.continuousAppointmentsByDay.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.all(),
        }),
      ]);
    },
    meta: {
      successMessage: "Termine wurden aufgefüllt",
      errorMessage: "Fehler beim Auffüllen des Dauertermins",
    } as const,
  });
};

export const useGapJustificationForTreatments = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    gapJustificationDto: GapJustificationDto[];
    rxId: string;
  };
  return useMutation({
    mutationFn: async ({ gapJustificationDto }: Vars) => {
      const response =
        await appointments.addOrUpdateGapJustificationForTreatments(gapJustificationDto);
      return response;
    },
    onSuccess: async (data, variables) => {
      // Justifications are patient-level and affect the gap badges in every
      // appointment list plus the rx validation. Invalidate by scope rather
      // than by detail key: the rxId from the appointment payload is not
      // guaranteed, and a missed key means a silently stale UI.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [appointmentKeys.appointmentsByRx.scope] }),
        queryClient.invalidateQueries({ queryKey: [appointmentKeys.appointmentsByPatient.scope] }),
        queryClient.invalidateQueries({
          queryKey: [appointmentKeys.filteredAppointmentsByPatient.scope],
        }),
        queryClient.invalidateQueries({ queryKey: [rxKeys.rx.scope] }),
        queryClient.invalidateQueries({ queryKey: [rxKeys.rxList.scope] }),
      ]);
    },
    meta: {
      successMessage: "Die Lücke wurde erfolgreich begründet",
      errorMessage: "Die Lücke konnte nicht begründet werden",
    } as const,
  });
};

export const useDeleteGapJustificationForTreatments = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();

  type Vars = {
    gapId: string;
    patientId: number;
    rxId: string;
  };
  return useMutation({
    mutationFn: async ({ gapId }: Vars) => {
      const response = await appointments.deleteGapJustification(gapId);
      return response;
    },
    onSuccess: async (data, variables) => {
      // Same scope-level invalidation as useGapJustificationForTreatments
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [appointmentKeys.appointmentsByRx.scope] }),
        queryClient.invalidateQueries({ queryKey: [appointmentKeys.appointmentsByPatient.scope] }),
        queryClient.invalidateQueries({
          queryKey: [appointmentKeys.filteredAppointmentsByPatient.scope],
        }),
        queryClient.invalidateQueries({ queryKey: [rxKeys.rx.scope] }),
        queryClient.invalidateQueries({ queryKey: [rxKeys.rxList.scope] }),
      ]);
    },
    meta: {
      successMessage: "Die Lücke wurde erfolgreich gelöscht",
      errorMessage: "Die Lücke konnte nicht gelöscht werden",
    } as const,
  });
};

// export const useSwapAppointments = () => {
//   const { scheduler } = useApi();
//   const queryClient = useQueryClient();
//   type Vars = {
//     seriesIds: string[];
//   };
//   return useMutation({
//     mutationKey: ["appointments", "swapAppointments"],
//     mutationFn: async ({ seriesIds }: Vars) => {
//       // The proxied API client already unwraps the AxiosResponse to its body,
//       // so `response` is the payload itself (not an AxiosResponse).
//       const data = (await scheduler.schedulerSwapAppointments(seriesIds)) as any;
//       const swapValidationErrors = data?.swapValidationErrors || [];
//       // "appointments were removed" signal: the backend lists every
//       // unscheduled perpetual (Dauerrezept) appointment it deleted (or kept due
//       // to an unresolved cancellation).
//       const perpetualAppointmentStatus = data?.perpetualAppointmentStatus || [];
//       return {
//         results: data?.results || [],
//         completableRxs: data?.completableRxs || [],
//         swapValidationErrors,
//         perpetualAppointmentStatus,
//         message: swapValidationErrors.length === 0 ? "Termine erfolgreich getauscht" : undefined,
//       };
//     },
//     onSuccess: async (data, variables) => {
//       await Promise.all([
//         queryClient.invalidateQueries({
//           queryKey: appointmentKeys.appointmentsBySeries.all(),
//         }),
//         queryClient.invalidateQueries({
//           queryKey: appointmentKeys.appointmentsByPatient.all(),
//         }),
//         queryClient.invalidateQueries({
//           queryKey: appointmentKeys.appointmentsByRx.all(),
//         }),
//         // Invalidates both the per-day (appointmentsForDate) and range
//         // (appointmentsForDateRange) calendar caches.
//         invalidateCalendarAppointmentCaches(queryClient),

//         // Swapping re-links treatments across Rxs and (with enforceAmounts)
//         // rewrites the therapy amounts, which changes the rx validation result
//         // (e.g. "Anzahl der Termine"). Invalidate the rx caches so the Rx form
//         // and its live validation refetch instead of showing stale amounts.
//         queryClient.invalidateQueries({ queryKey: [rxKeys.rx.scope] }),
//         queryClient.invalidateQueries({ queryKey: [rxKeys.rxList.scope] }),
//       ]);
//     },
//     meta: {
//       // The success snackbar is now driven dynamically by the `message` field
//       // returned from mutationFn (see above) so it can reflect the validation
//       // outcome. The global MutationCache falls back to `data.message` when
//       // meta.successMessage is absent.
//       errorMessage: "Der Termin konnte nicht ausgetauscht werden",
//     } as const,
//   });
// };

/**
 * Simplified swap hook: returns preview with errors, lets user choose to proceed.
 *
 * Flow:
 * 1. Call with force=false → Returns preview with assignments & errors
 * 2. UI shows errors to user
 * 3. User clicks "Proceed" → Call again with force=true → Commits
 * 4. User clicks "Cancel" → Do nothing
 */
export const useSimpleSwapAppointments = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();

  type Vars = {
    seriesIds: string[];
    previewMode?: boolean;
  };

  return useMutation({
    mutationKey: ["appointments", "simpleSwapAppointments"],
    mutationFn: async ({ seriesIds, previewMode = true }: Vars) => {
      const data = (await scheduler.schedulerSimpleSwapAppointments({
        seriesIds,
        previewMode,
      })) as any;
      return {
        preview: data?.preview ?? true,
        assignments: data?.assignments || [],
        computeErrors: data?.computeErrors || [],
        validationErrors: data?.validationErrors || [],
        warnings: data?.warnings || [],
        committed: data?.committed ?? false,
        commitCount: data?.commitCount ?? 0,
        commitErrors: data?.commitErrors || [],
        hasChanges: data?.hasChanges ?? false,
        // Success message only if committed
        message: data?.committed ? "Termine erfolgreich getauscht" : undefined,
      };
    },
    onSuccess: async (data, variables) => {
      // Only invalidate caches if we actually committed
      if (data.committed) {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.appointmentsBySeries.all(),
          }),
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.appointmentsByPatient.all(),
          }),
          queryClient.invalidateQueries({
            queryKey: appointmentKeys.appointmentsByRx.all(),
          }),
          invalidateCalendarAppointmentCaches(queryClient),
          queryClient.invalidateQueries({ queryKey: [rxKeys.rx.scope] }),
          queryClient.invalidateQueries({ queryKey: [rxKeys.rxList.scope] }),
        ]);
      }
    },
    meta: {
      errorMessage: "Der Termin konnte nicht ausgetauscht werden",
    } as const,
  });
};

export const useSaveAppointments = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  const locationId = useLocationId();
  type Vars = {
    appointmentList: AppointmentDto[];
  };
  return useMutation({
    mutationKey: ["appointments", "saveAppointments"],
    mutationFn: async ({ appointmentList }: Vars) => {
      // MD-286: stamp the viewed site onto every slot that has none yet, so
      // slots know which site they were planned at. Slots already stamped
      // keep their site; without a location context nothing is stamped and
      // the slot stays site-less (visible everywhere).
      const stamped = locationId
        ? appointmentList.map((app) => ({
            ...app,
            timeSlots: app.timeSlots?.map((ts: any) =>
              ts.locationId == null ? { ...ts, locationId } : ts
            ),
          }))
        : appointmentList;
      const response = await appointments.saveAppointments(stamped as AppointmentDto[]);
      return response;
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByRx.all(),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsBySeries.all(),
        }),
        invalidateCalendarAppointmentCaches(queryClient),
      ]);
    },
    meta: {
      successMessage: "Termin erfolgreich erstellt",
      errorMessage: "Fehler bei der Terminerstellung",
    } as const,
  });
};

export const useUnfuseAppointments = () => {
  const { scheduler } = useApi();
  const queryClient = useQueryClient();
  const calendarStore = useStore(CalendarContext);
  type Vars = {
    timeSlotId: string;
    isContinous: boolean;
    reason: string;
    patientId: number;
  };
  return useMutation({
    mutationKey: ["appointments", "unFuseAppointment"],
    mutationFn: async ({ timeSlotId, isContinous, reason, patientId }: Vars) => {
      const response: any = await scheduler.schedulerSplitTimeSlot(timeSlotId, reason, isContinous);
      return response;
    },
    onSuccess: async (data, variables) => {
      const invalidations = [
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.appointmentsByPatient.detail(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.patientBillsInfo.forPatient(variables.patientId),
        }),
        queryClient.invalidateQueries({
          queryKey: transactionKeys.unpaidBadgeCount.forPatient(variables.patientId),
        }),

        // invalidate calendar cache (TODO could be made more efficient)
        // TODO we need access to the date of the appointment to only invalidate the relevant day
        invalidateCalendarAppointmentCaches(queryClient),
        queryClient.invalidateQueries({
          queryKey: calendarKeys.unscheduledPatientAppointments.detail(variables.patientId),
        }),
      ];
      calendarStore.addToPinBoard(data); // Added to mainly handle unfused appointments when "Umziehen" is used.
      await Promise.all(invalidations);
    },
    meta: {
      successMessage: "Der Termin wurde erfolgreich entbündelt",
      errorMessage: "Der Termin konnte nicht entbündelt werden",
    } as const,
  });
};

export const useProcessAvailabilityRequest = () => {
  const { appointments } = useApi();
  const queryClient = useQueryClient();
  type Vars = {
    patientId: number;
    request: AvailabilityRequestDto;
  };
  return useMutation({
    mutationKey: ["appointments", "processAvailabilityRequest"],
    mutationFn: async ({ patientId, request }: Vars) => {
      const response = await appointments.processAvailabilityRequest(patientId, request);
      return response;
    },
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.patientUnavailabilityByPatient.detail(
            variables.patientId.toString(),
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: appointmentKeys.blockedSlotsByPatient.detail(variables.patientId.toString()),
        }),
      ]);
    },
    meta: {
      successMessage: "Verfügbarkeitsanfrage erfolgreich verarbeitet",
      errorMessage: "Verfügbarkeitsanfrage konnte nicht verarbeitet werden",
    } as const,
  });
};
